import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Plus, ChevronDown, ChevronRight, Trash2, Edit2 } from 'lucide-react';

const BEDROOM_OPTIONS = ['Studio','1 Bedroom','2 Bedroom','3 Bedroom','4 Bedroom','5 Bedroom'];
const UNIT_TYPE_OPTIONS = ['Apartment','Villa','Townhouse','Penthouse'];

export default function Properties() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tree, setTree] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showAddDev, setShowAddDev] = useState(false);
  const [showAddProject, setShowAddProject] = useState(null);
  const [showAddUnit, setShowAddUnit] = useState(null);
  const [devName, setDevName] = useState('');
  const [projectForm, setProjectForm] = useState({name:'',location:'',booking_start:'',description:''});
  const [unitForm, setUnitForm] = useState({bedroom:'Studio',unit_type:'Apartment',area_sqft:'',price_aed:''});

  const load = async () => {
    try {
      const res = await api.get('/api/v2/property-tree');
      setTree(res.data.tree || []);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setExpanded(prev => ({...prev, [id]: !prev[id]}));

  const addDeveloper = async () => {
    if (!devName.trim()) return;
    await api.post('/api/v2/developers', {name: devName});
    setDevName(''); setShowAddDev(false); load();
  };

  const addProject = async (devId) => {
    await api.post('/api/v2/projects', {...projectForm, developer_id: devId});
    setProjectForm({name:'',location:'',booking_start:'',description:''});
    setShowAddProject(null); load();
  };

  const addUnit = async (projectId) => {
    await api.post('/api/v2/unit-types', {...unitForm, project_id: projectId, area_sqft: parseFloat(unitForm.area_sqft)||0, price_aed: parseFloat(unitForm.price_aed)||0});
    setUnitForm({bedroom:'Studio',unit_type:'Apartment',area_sqft:'',price_aed:''});
    setShowAddUnit(null); load();
  };

  const archiveDev = async (id) => { if(window.confirm('Archive developer?')) { await api.delete('/api/v2/developers/'+id); load(); }};
  const archiveProject = async (id) => { if(window.confirm('Archive project?')) { await api.delete('/api/v2/projects/'+id); load(); }};
  const archiveUnit = async (id) => { await api.delete('/api/v2/unit-types/'+id); load(); };

  const fmt = (n) => n ? 'AED ' + Number(n).toLocaleString() : '-';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white">Properties</h3>
          <p className="text-dark-400 text-sm">Manage developers, projects and unit types</p>
        </div>
        {isAdmin && <button onClick={() => setShowAddDev(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm"><Plus className="w-4 h-4"/>Add Developer</button>}
      </div>

      {showAddDev && (
        <div className="glass rounded-xl p-4 flex gap-3">
          <input value={devName} onChange={e => setDevName(e.target.value)} placeholder="Developer / Builder name" className="flex-1 px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
          <button onClick={addDeveloper} className="px-4 py-2 bg-primary-500 text-dark-900 rounded-lg text-sm font-medium">Add</button>
          <button onClick={() => setShowAddDev(false)} className="px-4 py-2 bg-dark-700 text-white rounded-lg text-sm">Cancel</button>
        </div>
      )}

      {tree.length === 0 && <div className="glass rounded-xl p-8 text-center text-dark-400">No developers yet. Add one to get started.</div>}

      {tree.map(dev => (
        <div key={dev.id} className="glass rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-dark-700/30" onClick={() => toggle(dev.id)}>
            <div className="flex items-center gap-3">
              {expanded[dev.id] ? <ChevronDown className="w-4 h-4 text-primary-400"/> : <ChevronRight className="w-4 h-4 text-dark-400"/>}
              <span className="text-white font-semibold">{dev.name}</span>
              <span className="text-dark-400 text-xs">{dev.projects?.length || 0} projects</span>
            </div>
            {isAdmin && <div className="flex gap-2" onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowAddProject(dev.id)} className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-xs hover:bg-blue-500/20"><Plus className="w-3 h-3 inline"/> Project</button>
              <button onClick={() => archiveDev(dev.id)} className="p-1.5 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20"><Trash2 className="w-3 h-3"/></button>
            </div>}
          </div>

          {showAddProject === dev.id && (
            <div className="border-t border-dark-700 p-4 bg-dark-800/30">
              <p className="text-white text-sm font-medium mb-3">Add Project under {dev.name}</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input placeholder="Project name" value={projectForm.name} onChange={e => setProjectForm({...projectForm,name:e.target.value})} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"/>
                <input placeholder="Location" value={projectForm.location} onChange={e => setProjectForm({...projectForm,location:e.target.value})} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"/>
                <input placeholder="Booking start date" type="date" value={projectForm.booking_start} onChange={e => setProjectForm({...projectForm,booking_start:e.target.value})} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"/>
                <input placeholder="Description" value={projectForm.description} onChange={e => setProjectForm({...projectForm,description:e.target.value})} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"/>
              </div>
              <div className="flex gap-2">
                <button onClick={() => addProject(dev.id)} className="px-4 py-2 bg-primary-500 text-dark-900 rounded-lg text-sm font-medium">Add Project</button>
                <button onClick={() => setShowAddProject(null)} className="px-4 py-2 bg-dark-700 text-white rounded-lg text-sm">Cancel</button>
              </div>
            </div>
          )}

          {expanded[dev.id] && (
            <div className="border-t border-dark-700">
              {dev.projects?.length === 0 && <p className="text-dark-500 text-sm p-4">No projects yet.</p>}
              {dev.projects?.map(project => (
                <div key={project.id} className="border-b border-dark-700/50 last:border-b-0">
                  <div className="flex items-center justify-between p-3 pl-8 cursor-pointer hover:bg-dark-700/20" onClick={() => toggle(project.id)}>
                    <div className="flex items-center gap-3">
                      {expanded[project.id] ? <ChevronDown className="w-3 h-3 text-primary-400"/> : <ChevronRight className="w-3 h-3 text-dark-400"/>}
                      <span className="text-white text-sm font-medium">{project.name}</span>
                      {project.location && <span className="text-dark-400 text-xs">📍 {project.location}</span>}
                      {project.booking_start && <span className="text-dark-400 text-xs">📅 {project.booking_start}</span>}
                      <span className="text-dark-500 text-xs">{project.unit_types?.length || 0} units</span>
                    </div>
                    {isAdmin && <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                      <button onClick={() => setShowAddUnit(project.id)} className="px-2 py-1 bg-green-500/10 text-green-400 rounded text-xs hover:bg-green-500/20"><Plus className="w-3 h-3 inline"/> Unit</button>
                      <button onClick={() => archiveProject(project.id)} className="p-1 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20"><Trash2 className="w-3 h-3"/></button>
                    </div>}
                  </div>

                  {showAddUnit === project.id && (
                    <div className="p-3 pl-8 bg-dark-800/30 border-t border-dark-700/50">
                      <p className="text-white text-xs font-medium mb-2">Add Unit Type</p>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <select value={unitForm.bedroom} onChange={e => setUnitForm({...unitForm,bedroom:e.target.value})} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-xs focus:outline-none">
                          {BEDROOM_OPTIONS.map(b => <option key={b}>{b}</option>)}
                        </select>
                        <select value={unitForm.unit_type} onChange={e => setUnitForm({...unitForm,unit_type:e.target.value})} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-xs focus:outline-none">
                          {UNIT_TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
                        </select>
                        <input placeholder="Area (sqft)" type="number" value={unitForm.area_sqft} onChange={e => setUnitForm({...unitForm,area_sqft:e.target.value})} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-xs focus:outline-none"/>
                        <input placeholder="Price (AED)" type="number" value={unitForm.price_aed} onChange={e => setUnitForm({...unitForm,price_aed:e.target.value})} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-xs focus:outline-none"/>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => addUnit(project.id)} className="px-3 py-1.5 bg-primary-500 text-dark-900 rounded-lg text-xs font-medium">Add</button>
                        <button onClick={() => setShowAddUnit(null)} className="px-3 py-1.5 bg-dark-700 text-white rounded-lg text-xs">Cancel</button>
                      </div>
                    </div>
                  )}

                  {expanded[project.id] && (
                    <div className="pl-12 pr-4 pb-2">
                      {project.unit_types?.length === 0 && <p className="text-dark-500 text-xs py-2">No unit types yet.</p>}
                      <div className="grid grid-cols-1 gap-1">
                        {project.unit_types?.map(unit => (
                          <div key={unit.id} className="flex items-center justify-between py-2 px-3 bg-dark-800/30 rounded-lg">
                            <div className="flex items-center gap-4">
                              <span className="text-white text-xs">{unit.bedroom}</span>
                              <span className="text-dark-400 text-xs">{unit.unit_type}</span>
                              {unit.area_sqft > 0 && <span className="text-dark-400 text-xs">{unit.area_sqft} sqft</span>}
                              <span className="text-primary-400 text-xs font-medium">{fmt(unit.price_aed)}</span>
                            </div>
                            {isAdmin && <button onClick={() => archiveUnit(unit.id)} className="p-1 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20"><Trash2 className="w-3 h-3"/></button>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
